<script setup>
import GearViewer from "@/components/GearViewer.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { ref, computed, defineProps, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";

const store = useStore();

const props = defineProps({
    spd: Number,
    brake: Number,
    rpm: Number,
    throttle: Number,
    steeringAngle: Number,
    gear: Number,
    showLoader: Boolean
})

const lerpedValues = ref({
    spd: 0,
    brake: 0,
    rpm: 0,
    throttle: 0,
    steeringAngle: 0
})

const backgroundLoaded = ref(false)
const loaderLoaded = ref(false)

const isMounted = ref(false);
onMounted(() => {
    isMounted.value = true;

    doLerpUpdate();
})

onUnmounted(() => {
    isMounted.value = false;
})

const lerp = (start, end, amt) => (1 - amt) * start + amt * end;

function doLerpUpdate(){
    if(!isMounted.value) return;

    //console.log(props.brake)

    if(props.spd != null)
        lerpedValues.value.spd = lerp(lerpedValues.value.spd, props.spd, 0.1)

    if(props.rpm != null)
        lerpedValues.value.rpm = lerp(lerpedValues.value.rpm, props.rpm, 0.1)

    if(props.throttle != null)
        lerpedValues.value.throttle = lerp(lerpedValues.value.throttle, props.throttle, 0.1)

    if(props.steeringAngle != null)
        lerpedValues.value.steeringAngle = lerp(lerpedValues.value.steeringAngle, props.steeringAngle, 0.1)

    if(props.brake != null)
        lerpedValues.value.brake = lerp(lerpedValues.value.brake, props.brake, 0.1)
    requestAnimationFrame(doLerpUpdate);
}

const calcSpdPct = computed(() => {
    let speed = (lerpedValues.value.spd != null) ? lerpedValues.value.spd : 0;

    const min = -126;
    const max = 126;
    const fullScale = max - min;
    let scaledPct = fullScale / 360;
    return (speed / 240) * 100 * scaledPct;
});

const calcBrakePct = computed(() => {
    let brake = (lerpedValues.value.brake != null) ? lerpedValues.value.brake : 0;
    const min = -176;
    const max = -132;
    const fullScale = max - min;
    let scaledPct = fullScale / 360;
    return (brake/1200) * 100 * scaledPct;
});

const calcRpmPct = computed(() => {
    let rpm = (lerpedValues.value.rpm != null) ? lerpedValues.value.rpm : 0;
    const min = -93;
    const max = 93;
    const fullScale = max - min;
    let scaledPct = (fullScale / 360);
    return ((rpm) / 10000) * 100 * scaledPct;
});

const calcThrottlePct = computed(() => {
    let throttle = (lerpedValues.value.throttle != null) ? lerpedValues.value.throttle : 0;
    const min = 132;
    const max = 176;
    const fullScale = max - min;
    let scaledPct = (fullScale / 360);
    return (100 - ((throttle * 100) / 100)) * scaledPct;
});

const calcSteering = computed(() => {
    let steeringAngle = (lerpedValues.value.steeringAngle != null) ? lerpedValues.value.steeringAngle : 0;
    let scaledPct = 0.3;
    return steeringAngle * scaledPct;
});


</script>

<template>
    <transition name = "fade" mode = "in-out">
        <div class = "gauge-cluster flex-center" key = "loader" v-if = "props.showLoader">
            <transition name = "fade">
                <img class='full-image' v-show="loaderLoaded" @load="loaderLoaded = true"
                    src="../../public/assets/imgs/gauge-cluster-loading.png">
            </transition>
            <LoadingSpinner class = "loading-spinner"></LoadingSpinner>
        </div>
        <div class="gauge-cluster" key = "gauge-cluster" v-else>
            <img class='full-image spin' :style="`transform: rotate(${calcSteering.toFixed(0)}deg)`"
                src="../../public/assets/imgs/gauge-cluster-steering.png">

            <transition name="fade">
                <img class='full-image' v-show="backgroundLoaded" @load="backgroundLoaded = true"
                    src="../../public/assets/imgs/gauge-cluster-background.png">
            </transition>

            <img class='full-image' src="../../public/assets/imgs/gauge-speed-fill.png"
                :style="{ mask: `conic-gradient(from -126deg, black ${calcSpdPct}%, transparent ${calcSpdPct}%)` }">
            <img class='full-image' src="../../public/assets/imgs/gauge-rpm-fill.png"
                :style="{ mask: `conic-gradient(from -93deg, black ${calcRpmPct}%, transparent ${calcRpmPct}%)` }">
            <img class='full-image' src="../../public/assets/imgs/gauge-throttle-fill.png"
                :style="{ mask: `conic-gradient(from 132deg, transparent ${calcThrottlePct}%, black ${calcThrottlePct}%)` }">
            <img class='full-image' src="../../public/assets/imgs/gauge-brake-fill.png"
                :style="{ mask: `conic-gradient(from -176deg, black ${calcBrakePct}%, transparent ${calcBrakePct}%)` }">
            <div class="steering-angle">
                {{ lerpedValues.steeringAngle.toFixed(0) }}°
            </div>
            <div class="center-area">
                <div class="gear-value">
                    <GearViewer :gear="props.gear"></GearViewer>
                </div>
                <div class='gear-text'>
                    GEAR
                </div>
            </div>

            <div class="center-area">
                <div class="speed-value">
                    {{ lerpedValues.spd.toFixed(0) }}
                </div>
                <div class='speed-unit'>
                    MPH
                </div>
            </div>

            <div class="center-area">
                <div class="rpm-value">
                    {{ lerpedValues.rpm.toFixed(0) }}
                </div>
                <div class='rpm-unit'>
                    RPM
                </div>
            </div>
        </div>
    </transition>
</template>

<style lang="scss" scoped>
@import '../sass/GlobalClasses';
@import '../sass/Settings';

@property --progress-num {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 0%;
}

/* Optional: Define keyframes using the CSS property to animate the loading state */
@keyframes fillBar {
    from {
        --progress: 0%;
    }
}

.gauge-cluster {
    padding: 0;
    position: fixed;
    left: 0;
    top: vw(50);
    right: 0;
    bottom: 0;
    overflow: hidden;

    .loading-spinner{
        margin-top: vw(73);
        margin-left: 0;
    }

    .sponsored-by {
        position: absolute;
        width: vw(76);
        height: auto;
        top: vw(480);
    }

    .sponsored-image {
        position: absolute;
        width: vw(100);
        height: auto;
        top: vw(490);
    }

}

.steering-angle{
    position: absolute;
    transform: rotate(-90deg);
    color: white;
    left: vw(-8);
    top: 57%;
    text-align: center;
    width: vw(50);
}

.center-area{
    position: absolute;
    left: vw(220);
    right: vw(220);
    top: vw(232);
    bottom: vw(220);

    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    .gear-text{
        position: relative;
        color: white;
        font-size: vw(12);
        font-weight: 700;
        font-family: "Stainless";
    }

    .gear-value{
        position: relative;
        color: white;
        font-size: vw(35);
        margin-top: vw(210);
        font-family: "Stainless";
    }

    .speed-value {
        position: relative;
        color: white;
        font-size: vw(60);
        margin-top: vw(50);
        font-weight: 900;
        letter-spacing: 10%;
        font-family: "Stainless";
    }

    .speed-unit {
        position: relative;
        color: white;
        font-size: vw(12);
        margin-top: vw(-5);
        font-weight: 700;
        font-family: "Stainless";
    }

    .rpm-value{
        position: relative;
        color: white;
        font-size: vw(18);
        margin-top: vw(-90);
        font-weight: 900;
        letter-spacing: 10%;
        font-family: "Stainless";

    }

    .rpm-unit{
        position: relative;
        color: white;
        font-size: vw(12);
        margin-top: vw(0);
        font-weight: 700;
        font-family: "Stainless";

    }

}



.center-circle{
    //mask: conic-gradient(from -126deg, black 25%, transparent 25%);
}

.spin{
    transform-origin: 50% 50.7%;
}

.full-image{
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: auto;

    transition: all ease 0.2s;
}

.half-image{
    clip-path: circle(50% at right);
}

.radial-svg {
    transform: rotate(-90deg);
    /* Rotates the starting point to the top (12 o'clock) */
}

circle {
    fill: none;
    stroke-width: 12px;
}

.radial-bg {
    stroke: #e6e6e6;
    /* Gray background track */
}

.radial-progress {
    stroke: #007bff;
    /* Vibrant blue progress track */
    stroke-linecap: round;
    /* Gives the bar smooth, rounded tips */
    stroke-dasharray: var(--circle-circumference);
    stroke-dashoffset: var(--circle-circumference);
    animation: fillProgress 1.5s ease-out forwards;
}

</style>
